import { css } from "@linaria/core";
import React, { memo, useCallback, useMemo } from "react";
import { useTranslations } from "../../../../../../../../../api/useTranslations";
import t from "../../../../../../../../../i18n";
import Ellipsis from "@ui/Ellipsis";
import Select, { type SelectOption } from "@ui/Select";
import { Tag } from "@ui/Tag";
import { colors, spacings } from "@ui/Theme";
import { useDashboardEditorContext } from "../../../../../../hook/context/editor";
import {
  AGGREGATE_NO_SYSTEM,
  AGGREGATE_UNEXPLAINED_LOSSES,
  useAvailableResources,
} from "../../../../../../hook/useAvailableResources";
import type { BlockDiagramModelT } from "../../../../../../schema/block/diagram";
import type { QuantityColumnsT } from "../../../../../../schema/resource/columns";
import { getAvailableResourceKind } from "../../../../../../utils/getAvailableResourceKind";
import Button from "@ui/Button";
import { ShareBoxLine } from "@ui/IconV2/react";
import { toPath } from "../../../../../../../../../router/helpers/path";
import { toBasenamePath } from "../../../../../../../../../router/helpers/navigation";

const DEFAULT_QUANTITY_COLUMN: QuantityColumnsT[] = ["measured", "expected", "gap", "validity"];

export const AnnotationLayerContentSettings = memo(
  ({
    projectId,
    block,
    annotation,
  }: {
    projectId: string;
    block: BlockDiagramModelT;
    annotation: BlockDiagramModelT["config"]["layers"][number];
  }) => {
    const availableResources = useAvailableResources({ projectId });
    const editor = useDashboardEditorContext();
    const translations = useTranslations();

    const contentOptions: SelectOption[] = useMemo(() => {
      const inputs = [
        availableResources.quantity,
        availableResources.fault,
        availableResources.system,
        availableResources.aggregate,
      ];

      return inputs.flatMap(({ kind, identifiers }) =>
        identifiers.map(identifier => {
          const isUnexplainedLoss = identifier.name === AGGREGATE_UNEXPLAINED_LOSSES;
          const isNoneSystem = identifier.name === AGGREGATE_NO_SYSTEM;
          const identifierName =
            "displayName" in identifier && identifier.displayName
              ? identifier.displayName
              : identifier.name;

          const label = isUnexplainedLoss
            ? t("charts.legend.unexplained_losses", translations)
            : isNoneSystem
              ? t("project.dashboard.editor.gui.no_system")
              : identifierName;

          const description = t(
            "project.dashboard.encoding.kind",
            kind,
            "manual" in identifier ? identifier.manual : false,
          );

          const extra = (
            <>
              {kind === "QUANTITY" && identifier.unit && <Tag size="s">{identifier.unit}</Tag>}
              {"system" in identifier && identifier.system && (
                <Tag size="s">
                  <Ellipsis>{identifier.system}</Ellipsis>
                </Tag>
              )}
            </>
          );

          const disabled = block.config.layers.some(l =>
            l.encodings.some(e => e.identifier === identifier.name),
          );

          return {
            label,
            value: identifier.name,
            description,
            extra,
            disabled,
          };
        }),
      );
    }, [
      availableResources.aggregate,
      availableResources.fault,
      availableResources.quantity,
      availableResources.system,
      block.config.layers,
      translations,
    ]);

    const addAnnotationContent = useCallback(
      (specifiedIdentifier: string) => {
        /**
         * @note Updates concerning the distribution of events across layers and transforms are made in AnnotationSettings
         */

        if (!block) return;
        const kind = getAvailableResourceKind(availableResources, specifiedIdentifier);

        /**
         * UPDATE LAYERS
         * ======================================================================
         */
        const updatedLayers = block.config.layers.map(layer => {
          if (layer !== annotation) return layer;
          switch (kind) {
            case "QUANTITY": {
              const isSimulationOutput =
                availableResources.quantity.identifiers.find(q => q.name === specifiedIdentifier)
                  ?.usedForSimulationOutputs ?? false;

              const columns = DEFAULT_QUANTITY_COLUMN.filter(column =>
                isSimulationOutput ? true : column !== "gap" && column !== "expected",
              );

              return {
                ...layer,
                encodings: columns.map(column => ({ identifier: specifiedIdentifier, column })),
                thresholds: [],
                evolutions: [],
                showLabel: true,
                showDescription: true,
              };
            }
            case "FAULT":
              return {
                ...layer,
                encodings: [
                  {
                    identifier: specifiedIdentifier,
                    column: "faultStatus",
                  },
                  ...(availableResources.fault.identifiers.find(
                    identifier => identifier.name === specifiedIdentifier && identifier.manual,
                  )
                    ? []
                    : [
                        {
                          identifier: specifiedIdentifier,
                          column: "real",
                        },
                      ]),
                  ...availableResources.performanceIndicators.map(pi => ({
                    identifier: specifiedIdentifier,
                    column: "quantityImpacts",
                    quantity: pi.name,
                  })),
                ],
                thresholds: [],
                evolutions: [],
                showLabel: true,
                showDescription: true,
              };
            case "SYSTEM":
              return {
                ...layer,
                encodings: availableResources.performanceIndicators.map(pi => ({
                  identifier: specifiedIdentifier,
                  column: "systemImpact",
                  quantity: pi.name,
                })),
                thresholds: [],
                evolutions: [],
                showLabel: true,
                showDescription: true,
              };
            case "AGGREGATE":
              return {
                ...layer,
                encodings: availableResources.performanceIndicators.map(pi => ({
                  identifier: specifiedIdentifier,
                  column: "unexplainedLosses",
                  quantity: pi.name,
                })),
                thresholds: [],
                evolutions: [],
                showLabel: true,
                showDescription: true,
              };
            default:
              return layer;
          }
        });

        /**
         * UPDATE TRANSFORMS
         * ======================================================================
         */
        const identifiers = updatedLayers.flatMap(l => l.encodings.map(e => e.identifier));
        const updatedTransforms = [...(block.data.transforms ?? [])].filter(t => {
          const systemIds = t.filter?.flatMap(f => f.value);
          return identifiers.some(identifier => systemIds?.includes(identifier));
        });
        switch (kind) {
          case "SYSTEM":
            updatedTransforms.push({
              aggregate: [
                {
                  operation: "sum" as const,
                  column: "quantityImpacts",
                  as: "systemImpact",
                },
              ],
              groupBy: ["systemId", "date"],
              filter: [
                {
                  column: "faultStatus",
                  operation: "oneOf",
                  value: ["CONFIRMED", "NON_MODELLED"],
                },
                {
                  column: "systemId",
                  operation: "oneOf",
                  value: [specifiedIdentifier],
                },
              ],
            });
            break;
          case "AGGREGATE":
            if (specifiedIdentifier === AGGREGATE_UNEXPLAINED_LOSSES) {
              updatedTransforms.push({
                aggregate: [
                  {
                    operation: "sum" as const,
                    column: "quantityImpacts",
                    as: "explainedLosses",
                  },
                ],
                groupBy: ["date"],
                filter: [
                  {
                    column: "faultStatus",
                    operation: "oneOf",
                    value: ["CONFIRMED", "NON_MODELLED"],
                  },
                ],
              });
            }
            break;
          default:
            break;
        }

        const updatedBlock: BlockDiagramModelT = {
          ...block,
          data: {
            ...block.data,
            transforms: updatedTransforms.filter(t => t.filter?.values),
          },
          config: {
            ...block.config,
            layers: updatedLayers,
          },
        };
        void editor?.updateBlock(updatedBlock);
      },
      [block, availableResources, editor, annotation],
    );

    const selectedIdentifier = useMemo(
      () => annotation.encodings[0]?.identifier ?? null,
      [annotation.encodings],
    );

    return (
      <Select
        label={t("project.dashboard.editor.gui.section.title.content")}
        options={contentOptions}
        value={selectedIdentifier ?? ""}
        onChange={e => addAnnotationContent(e.target.value)}
        prompt=""
        className={annotationContentSelector}
        filtered
        clearable={false}
        bottomSlot={
          <>
            <div>
              <hr className={inlineSeparator} />
            </div>
            <div className={footerSlot}>
              <Button
                variant="link"
                leftIcon={<ShareBoxLine />}
                slim
                className={resourceSelectorMenuCreateShortcut}
                onClick={() =>
                  window.open(
                    toBasenamePath(
                      toPath("/projects/:projectId/settings/quantities", {
                        projectId,
                      }),
                    ),
                  )
                }
              >
                {t("project.dashboard.resource.open_quantities")}
              </Button>
            </div>
          </>
        }
      />
    );
  },
);

const inlineSeparator = css`
  background: ${colors.titanium100};
  border: none;
  height: 1px;
  margin: 0 ${spacings.s};
`;

const footerSlot = css`
  position: sticky;
  bottom: 0;
  background-color: ${colors.hydrogen};
  padding: ${spacings.s};
  z-index: 1;
`;

const resourceSelectorMenuCreateShortcut = css`
  display: block;
  margin: auto;
`;

const annotationContentSelector = css`
  margin-top: ${spacings.m};
`;
